import React from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Plus, X, RotateCcw } from 'lucide-react';
import {
  BannerVisualOverride,
  BannerVisualStyle,
  resolveBannerVisual,
  BannerPreset,
  BannerCustomStyle,
} from '../../../api/banners';

interface BannerColorEditorProps {
  /** Какую тему приложения редактируем. */
  mode: 'light' | 'dark';
  preset: BannerPreset;
  custom?: BannerCustomStyle | null;
  onChange: (next: BannerVisualOverride | null) => void;
}

const FIELDS: Array<{ key: keyof BannerVisualStyle; label: string }> = [
  { key: 'fg', label: 'Заголовок' },
  { key: 'muted', label: 'Подзаголовок' },
  { key: 'iconBg', label: 'Фон иконки' },
  { key: 'iconFg', label: 'Цвет иконки' },
];

/**
 * Ручные цвета поверх пресета для одной темы. Пустое поле = берём из пресета,
 * поэтому админ может поменять только фон, не трогая остальное.
 */
export function BannerColorEditor({
  mode,
  preset,
  custom,
  onChange,
}: BannerColorEditorProps) {
  const override = (mode === 'dark' ? custom?.dark : custom?.light) ?? {};
  // Значения из пресета — их показываем как плейсхолдеры, чтобы было видно,
  // что именно перекрывается.
  const base = resolveBannerVisual(preset, mode === 'dark', null);

  const colors = override.colors?.length ? override.colors : base.colors;
  const hasOverride = Object.values(override).some(
    (v) => v !== undefined && v !== null && v !== '',
  );

  const patch = (next: Partial<BannerVisualOverride>) => {
    const merged = { ...override, ...next };
    const cleaned = Object.fromEntries(
      Object.entries(merged).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '',
      ),
    ) as BannerVisualOverride;
    onChange(Object.keys(cleaned).length > 0 ? cleaned : null);
  };

  const setColorStop = (index: number, value: string) => {
    const next = [...colors];
    next[index] = value;
    patch({ colors: next });
  };

  const labelCls = 'text-xs text-text-2';
  const title = mode === 'dark' ? 'Тёмная тема' : 'Светлая тема';

  return (
    <fieldset className="m-0 flex flex-col gap-3 rounded-xl border border-border-soft p-3">
      <legend className="sr-only">{title}</legend>
      <div className="flex min-h-8 items-center justify-between">
        <span aria-hidden className="text-[13px] font-semibold">{title}</span>
        {hasOverride && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
          >
            <RotateCcw aria-hidden />
            Вернуть пресет
          </Button>
        )}
      </div>

      {/* Фон: 1 цвет — заливка, 2+ — градиент */}
      <div className="flex flex-col gap-1.5">
        <span className={labelCls}>
          Фон {colors.length > 1 ? `(градиент, ${colors.length} цвета)` : '(заливка)'}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {colors.map((color, index) => (
            <div key={index} className="flex items-center gap-1">
              <input
                type="color"
                value={normalizeHex(color)}
                onChange={(e) => setColorStop(index, e.target.value)}
                className="h-8 w-10 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                title={color}
                aria-label={`${title}: цвет фона ${index + 1}`}
              />
              {colors.length > 1 && (
                <button
                  type="button"
                  onClick={() => patch({ colors: colors.filter((_, i) => i !== index) })}
                  className="grid size-6 place-items-center rounded-md text-text-2 outline-none hover:bg-neg-soft hover:text-neg focus-visible:ring-2 focus-visible:ring-ring"
                  title="Убрать цвет"
                  aria-label={`Убрать цвет фона ${index + 1}`}
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              )}
            </div>
          ))}
          {colors.length < 4 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => patch({ colors: [...colors, colors[colors.length - 1]] })}
            >
              <Plus aria-hidden />
              Цвет
            </Button>
          )}
        </div>
      </div>

      {/* Остальные цвета */}
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(({ key, label }) => {
          const value = override[key as keyof BannerVisualOverride] as string | undefined;
          const fallback = base[key] as string;
          return (
            <div key={key} className="flex flex-col gap-1">
              <label htmlFor={`bc-${mode}-${key}`} className={labelCls}>{label}</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={normalizeHex(value || fallback)}
                  onChange={(e) => patch({ [key]: e.target.value })}
                  className="h-8 w-10 shrink-0 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                  aria-label={`${title}: ${label}, палитра`}
                />
                <Input
                  value={value ?? ''}
                  placeholder={fallback}
                  id={`bc-${mode}-${key}`}
                  onChange={(e) => patch({ [key]: e.target.value })}
                  className="h-8 font-mono text-xs"
                />
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * <input type="color"> понимает только #rrggbb. Полупрозрачные значения из
 * пресетов (rgba) он бы обнулил в чёрный, поэтому для них подставляем серый —
 * точное значение всё равно правится текстовым полем.
 */
function normalizeHex(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#888888';
}
